/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════
// InlineCatalogPicker — popover Airtable-style pour sélectionner une option
// Mode single (statut, type, greffe, cabinet) ou multi (sous-types).
// Supporte un theme="dark" pour usage dans les pages admin sombres.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import CatalogPill from './CatalogPill';

// Tokens dark (cohérents avec Admin.js)
const DARK = {
  bg:       '#1C1C28',
  bgSec:    '#252533',
  border:   '#3A3A4A',
  text:     '#EDEAE4',
  textSec:  '#B8B8C4',
  textTer:  '#7E7E8E',
  or:       '#D4A843',
};

export default function InlineCatalogPicker({
  currentValue,
  options,
  multi = false,
  onChange,
  disabled = false,
  placeholder = '+ Choisir',
  size = 'sm',
  emptyLabel = '—',
  theme = 'light',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const isDark = theme === 'dark';

  const C = isDark ? {
    bg: DARK.bg, bgSec: DARK.bgSec, border: DARK.border,
    text: DARK.text, textSec: DARK.textSec, textTer: DARK.textTer,
    accent: DARK.or, accentBg: 'rgba(212,168,67,0.10)', accentBorder: '#D4A843',
    hoverBg: DARK.bgSec,
  } : {
    bg: 'var(--color-background-primary)',
    bgSec: 'var(--color-background-secondary)',
    border: 'var(--color-border-primary)',
    text: 'var(--color-text-primary)',
    textSec: 'var(--color-text-secondary)',
    textTer: 'var(--color-text-tertiary)',
    accent: '#B8860B',
    accentBg: 'rgba(184,134,11,0.04)',
    accentBorder: '#B8860B',
    hoverBg: 'var(--color-background-secondary)',
  };

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = (options || []).filter(o =>
    !search || (o.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const isSelected = (id) => {
    if (multi) return Array.isArray(currentValue) && currentValue.includes(id);
    return currentValue === id;
  };

  const handleSelect = (id) => {
    if (multi) {
      const arr = Array.isArray(currentValue) ? currentValue : [];
      const newArr = arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
      onChange(newArr);
    } else {
      onChange(id);
      setOpen(false);
      setSearch('');
    }
  };

  const handleClear = () => {
    onChange(multi ? [] : null);
    if (!multi) setOpen(false);
  };

  const selectedItems = (() => {
    if (multi) {
      const arr = Array.isArray(currentValue) ? currentValue : [];
      return arr.map(id => (options || []).find(o => o.id === id)).filter(Boolean);
    }
    if (!currentValue) return [];
    const found = (options || []).find(o => o.id === currentValue);
    return found ? [found] : [];
  })();

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onClick={() => !disabled && setOpen(!open)}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          padding: '3px 4px',
          borderRadius: '6px',
          border: open ? `1px solid ${C.accentBorder}` : '1px solid transparent',
          background: open ? C.accentBg : 'transparent',
          transition: 'all 0.1s',
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: '3px',
          alignItems: 'center',
          minWidth: '60px',
        }}
        onMouseEnter={e => { if (!disabled && !open) e.currentTarget.style.background = C.hoverBg; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        {selectedItems.length === 0 ? (
          <span style={{ fontSize: '12px', color: disabled ? C.textTer : C.textSec, fontStyle: disabled ? 'normal' : 'italic' }}>
            {disabled ? emptyLabel : placeholder}
          </span>
        ) : (
          selectedItems.map(it => (
            <CatalogPill
              key={it.id}
              label={it.name}
              color={it.color}
              emoji={it.emoji}
              size={size}
              removable={!disabled && multi}
              onRemove={() => handleSelect(it.id)}
            />
          ))
        )}
      </div>

      {open && !disabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          minWidth: '240px',
          maxWidth: '320px',
          // Fond OPAQUE forcé : sinon, dans certains contextes (drawer modal,
          // overlay sombre), la variable CSS résout en transparent et le
          // dropdown laisse voir le contenu derrière.
          background: isDark ? C.bg : '#ffffff',
          border: `0.5px solid ${C.border}`,
          borderRadius: '8px',
          boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 1000,
          padding: '6px',
        }}>
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..."
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '6px 10px', fontSize: '12px',
              border: `0.5px solid ${C.border}`,
              borderRadius: '5px',
              outline: 'none',
              marginBottom: '4px',
              background: isDark ? C.bgSec : '#ffffff',
              color: C.text,
            }}
          />
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: C.textTer }}>
                Aucune option
              </div>
            ) : (
              filtered.map(o => (
                <div
                  key={o.id}
                  onClick={() => handleSelect(o.id)}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: isSelected(o.id) ? C.accentBg : 'transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = isSelected(o.id) ? C.accentBg : C.hoverBg}
                  onMouseLeave={e => e.currentTarget.style.background = isSelected(o.id) ? C.accentBg : 'transparent'}
                >
                  {multi && (
                    <input
                      type="checkbox"
                      checked={isSelected(o.id)}
                      readOnly
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  <CatalogPill label={o.name} color={o.color} emoji={o.emoji} size={size} />
                </div>
              ))
            )}
          </div>
          {(currentValue || (multi && selectedItems.length > 0)) && (
            <div style={{ borderTop: `0.5px solid ${C.border}`, marginTop: '4px', paddingTop: '4px' }}>
              <div onClick={handleClear} style={{
                padding: '5px 8px', cursor: 'pointer', fontSize: '11px',
                color: C.textTer, borderRadius: '4px',
              }}
              onMouseEnter={e => e.currentTarget.style.background = C.hoverBg}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {multi ? 'Tout désélectionner' : 'Effacer'}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
